import React from 'react';
import { FiGlobe, FiCopy, FiTrendingUp, FiChevronRight, FiSettings, FiZap, FiAlertOctagon } from 'react-icons/fi';
import { clsx } from 'clsx';
import SafeIcon from '../common/SafeIcon';

const DNSIntelligence = ({ stats, isRunning }) => {
  const { dnsLatencies: latencies, dailyAverages, ping, jitter, packetLoss } = stats;

  const providers = [
    { name: 'Cloudflare', ip: '1.1.1.1', hostname: '1dot1dot1dot1.cloudflare-dns.com', latency: latencies['1.1.1.1'], avg: dailyAverages['1.1.1.1'] },
    { name: 'Google', ip: '8.8.8.8', hostname: 'dns.google', latency: latencies['8.8.8.8'], avg: dailyAverages['8.8.8.8'] },
    { name: 'Quad9', ip: '9.9.9.9', hostname: 'dns.quad9.net', latency: latencies['9.9.9.9'], avg: dailyAverages['9.9.9.9'] },
  ];

  const getSpeedPlaybook = () => {
    const actions = [];
    const sortedDNS = Object.entries(latencies)
      .filter(([_, v]) => v > 0)
      .sort((a, b) => a[1] - b[1]);

    if (sortedDNS.length > 0) {
      actions.push({
        title: 'Switch Private DNS',
        desc: `Fastest node detected: ${sortedDNS[0][0]} (${sortedDNS[0][1]}ms). Update hostname for ~${Math.round((ping - sortedDNS[0][1]) / (ping || 1) * 100)}% uplift.`,
        icon: FiSettings
      });
    }

    if (jitter > 15) {
      actions.push({
        title: 'Network Stabilization',
        desc: 'High jitter detected. Switch to a 5GHz band or use Ethernet to stabilize AI streams.',
        icon: FiZap
      });
    } else if (packetLoss > 0) {
      actions.push({
        title: 'Hardware Audit',
        desc: 'Packet loss detected. Check physical cable seating or restart edge router.',
        icon: FiAlertOctagon
      });
    }
    return actions;
  };

  const playbook = getSpeedPlaybook();

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert(`Copied: ${text}\nSet this as your Private DNS hostname.`);
  };

  return (
    <div className={clsx("space-y-6 transition-opacity duration-500", !isRunning && "opacity-50 grayscale pointer-events-none")}>
      <div className="space-y-4">
        <div className="flex items-center justify-between px-1">
          <h3 className="text-xs font-bold text-muted uppercase tracking-widest flex items-center gap-2">
            <SafeIcon icon={FiGlobe} className="text-primary" /> Private DNS Intelligence
          </h3>
          <div className="flex items-center gap-1 text-[10px] text-primary font-mono bg-primary/10 px-2 py-0.5 rounded border border-primary/20">
            <SafeIcon icon={FiTrendingUp} size={10} /> DAILY ANALYTICS
          </div>
        </div>

        <div className="grid gap-3">
          {providers.map((p) => {
            const isError = !p.latency || p.latency === -1;
            return (
              <div key={p.ip} className="glass-panel rounded-xl p-3 border border-white/5 relative group">
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-2">
                    <div className={clsx("w-2 h-2 rounded-full shadow-[0_0_8px]", isError ? "bg-danger shadow-danger/50" : "bg-primary shadow-primary/50")} />
                    <span className="text-sm font-bold text-text">{p.name}</span>
                  </div>
                  <div className="flex gap-3 text-right">
                    <div>
                      <div className="text-[9px] text-muted uppercase font-bold leading-none mb-1">Live</div>
                      <div className={clsx("text-xs font-mono font-bold", isError ? "text-danger" : "text-primary")}>
                        {isError ? 'OFFLINE' : `${p.latency}ms`}
                      </div>
                    </div>
                    <div className="w-px h-6 bg-white/10" />
                    <div>
                      <div className="text-[9px] text-muted uppercase font-bold leading-none mb-1">Daily Avg</div>
                      <div className="text-xs font-mono font-bold text-text">
                        {p.avg > 0 ? `${p.avg}ms` : '--'}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-black/40 rounded-lg p-2 border border-white/5">
                  <code className="text-[11px] text-primary/90 font-mono truncate">{p.hostname}</code>
                  <button onClick={() => copyToClipboard(p.hostname)} className="p-1 px-2 text-[10px] bg-white/5 hover:bg-white/10 rounded border border-white/10 text-muted transition-all flex items-center gap-1">
                    <SafeIcon icon={FiCopy} size={10} /> COPY
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Speed Playbook Integrated at Bottom */}
      <div className="space-y-3 pt-2">
        <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted px-1">Optimization Playbook</h3>
        {playbook.length > 0 ? playbook.map((action, i) => (
          <div key={i} className="glass-panel p-3 rounded-xl border border-white/5 group hover:border-primary/30 transition-all">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-white/5 text-muted group-hover:text-primary transition-colors">
                <SafeIcon icon={action.icon} size={14} />
              </div>
              <div className="space-y-1">
                <h4 className="text-[11px] font-black uppercase text-white tracking-tight flex items-center gap-2">
                  {action.title}
                  <FiChevronRight className="opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                </h4>
                <p className="text-[11px] text-muted leading-relaxed font-medium">
                  {action.desc}
                </p>
              </div>
            </div>
          </div>
        )) : (
          <div className="p-4 text-center border border-dashed border-white/10 rounded-xl">
             <p className="text-[10px] text-muted font-bold uppercase tracking-widest">System performing at peak capacity</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DNSIntelligence;